<template>
  <div class="print">
    <!-- 1. 헤더 및 결재란 -->
    <section class="section-approval">
      <div class="header-left-area">
        <p class="print-title">{{ $t('관리감독자 순회점검표') }}</p>
        <div class="division-title mt-10">□ {{ $t('기본정보') }}</div>
      </div>

      <table class="table-approval-field mt-auto">
        <colgroup>
          <col width="38" />
          <col width="112" />
          <col width="112" />
          <col width="112" />
          <col width="112" />
        </colgroup>
        <tr>
          <th rowspan="2" class="gray p-0" style="border-bottom: none;">
            <div class="flex justify-center items-center">
              <p style="writing-mode: vertical-lr">{{ $i18n.locale === 'ko' ? $t('결&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;재') : $t('결재') }}</p>
            </div>
          </th>
          <th class="gray">{{ $t('작성자') }}</th>
          <th class="gray">{{ $t('검토자') }}</th>
          <th class="gray">{{ $t('회람자') }}</th>
          <th class="gray">{{ $t('승인자') }}</th>
        </tr>
        <tr>
          <td style="vertical-align: middle; height: 100px; border-bottom: none;">
            <div class="signature-container">
              <img v-if="form.sign1.sign" :src="form.sign1.sign" class="signature-image" />
              <p v-else-if="form.sign1.name" class="name-text">{{ form.sign1.name }}</p>
            </div>
          </td>
          <td style="vertical-align: middle; height: 100px; border-bottom: none;">
            <div class="signature-container">
              <img v-if="form.sign2.sign" :src="form.sign2.sign" class="signature-image" />
              <p v-else-if="form.sign2.name" class="name-text">{{ form.sign2.name }}</p>
            </div>
          </td>
          <td style="vertical-align: middle; height: 100px; border-bottom: none;">
            <div class="signature-container">
              <img v-if="form.sign3.sign" :src="form.sign3.sign" class="signature-image" />
              <p v-else-if="form.sign3.name" class="name-text">{{ form.sign3.name }}</p>
            </div>
          </td>
          <td style="vertical-align: middle; height: 100px; border-bottom: none;">
            <div class="signature-container">
              <img v-if="form.sign4.sign" :src="form.sign4.sign" class="signature-image" />
              <p v-else-if="form.sign4.name" class="name-text">{{ form.sign4.name }}</p>
            </div>
          </td>
        </tr>
      </table>
    </section>

    <!-- 2. 수행평가 대상 (기본 정보) -->
    <section class="section-stdInfo mb-5">
      <table class="w-full">
        <colgroup>
          <col width="140" />
          <col />
          <col width="140" />
          <col />
        </colgroup>
        <tr>
          <th>{{ $t('회사명') }}</th>
          <td class="text-left px-2">{{ form.stdInfo.field_name }}</td>
          <th>{{ $t('작성자 성명') }}</th>
          <td class="text-left px-2">{{ form.stdInfo.writer_name }}</td>
        </tr>
        <tr>
          <th>{{ $t('작성일') }}</th>
          <td class="text-left px-2">{{ form.stdInfo.date }}</td>
          <th>{{ $t('평가일') }}</th>
          <td class="text-left px-2">{{ form.inputA_6 }}</td>
        </tr>
        <tr>
          <th>{{ $t('부서(업체)명') }}</th>
          <td class="text-left px-2">{{ form.inputA_1 }}</td>
          <th>{{ $t('피평가자') }}</th>
          <td class="text-left px-2">{{ form.inputA_5 }}</td>
        </tr>
      </table>
    </section>

    <!-- 3. 업무 수행 여부 (점검표) -->
    <section class="section-inspect mb-10">
      <div class="division-title">□ {{ $t('업무 수행 여부') }}</div>
      <table class="w-full">
        <colgroup>
          <col width="60" />
          <col width="140" />
          <col />
          <col width="60" />
          <col width="60" />
          <col width="60" />
          <col width="200" />
        </colgroup>
        <thead>
          <tr>
            <th>{{ $t('순번') }}</th>
            <th>{{ $t('근거') }}</th>
            <th>{{ $t('점검항목') }}</th>
            <th>{{ $t('Yes') }}</th>
            <th>{{ $t('No') }}</th>
            <th>{{ $t('N/A') }}</th>
            <th>{{ $t('개선요망 사항') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in form.inspectList" :key="'inspect_' + index">
            <td>{{ item.col1 }}</td>
            <td v-html="item.col2"></td>
            <td class="text-left py-2 px-4" v-html="item.label"></td>
            <td>
              <PrintCheckbox :value="item.is_checked === 'Yes'" class="justify-center" />
            </td>
            <td>
              <PrintCheckbox :value="item.is_checked === 'No'" class="justify-center" />
            </td>
            <td>
              <PrintCheckbox :value="item.is_checked === 'N/A'" class="justify-center" />
            </td>
            <td class="text-left">{{ item.note || "-" }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="7" class="text-left p-4 bg-gray-50">
              {{ $t('※ (중처법=중대재해처벌 등에 관한 법률. 산안법=산업안전보건법)') }}
            </td>
          </tr>
        </tfoot>
      </table>
    </section>

    <!-- 4. 사진대지 -->
    <section class="section-photo mb-10 page-break" v-if="form.photoList && form.photoList.length > 0">
      <div class="division-title">□ {{ $t('사진대지') }}</div>
      <table class="w-full">
        <colgroup>
          <col width="100" />
          <col />
        </colgroup>
        <template v-for="(photo, index) in form.photoList">
          <tr :key="'photo_' + index" v-if="photo.path">
            <td colspan="2" style="height: 500px">
              <img :src="photo.path" class="max-w-full h-full object-contain mx-auto block" />
            </td>
          </tr>
          <tr :key="'desc_' + index" v-if="photo.path">
            <th>{{ $t('내용') }}</th>
            <td class="text-left">{{ photo.input }}</td>
          </tr>
        </template>
      </table>
    </section>

    <!-- 5. 파일첨부 -->
    <section class="section-file mb-10" v-if="form.fileList && form.fileList.length > 0">
      <div class="division-title">□ {{ $t('첨부파일') }}</div>
      <table class="w-full">
        <colgroup>
          <col width="160" />
          <col />
        </colgroup>
        <tr v-for="(file, index) in form.fileList" :key="'file_' + index">
          <th>{{ $t('파일명') }}</th>
          <td class="text-left">{{ file.fileName }}</td>
        </tr>
      </table>
    </section>
  </div>
</template>

<script>
import PrintCheckbox from "@/components/print/PrintCheckbox.vue";

export default {
  name: "Print",
  components: { PrintCheckbox },
  props: {
    isPrint: Boolean,
    board: Object,
    form: Object,
  },
};
</script>

<style lang="scss" scoped>
.print {
  .print-title {
    font-size: 32px;
    font-weight: bold;
    color: #000;
    text-align: center;
    padding: 20px 0;
  }
  .division-title {
    font-size: 24px;
    font-weight: bold;
    color: #3c3c3c;
    padding: 1.25rem 0rem;
  }

  .signature-container {
    width: 100%;
    height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10px;

    .signature-image {
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 80px;
      object-fit: contain;
      display: block;
    }
    .name-text {
      font-size: 16px;
      margin-top: 5px;
    }
  }

  table {
    table-layout: fixed;
    border-collapse: collapse;
    width: 100%;

    th,
    td {
      border: 1px solid #757575;
      text-align: center;
      font-size: 18px;
      padding: 10px;
      vertical-align: middle;
    }
    th {
      background-color: #ececec;
      font-weight: 500;
    }
    td {
      word-break: break-all;
    }
    .gray {
      background-color: #ececec;
    }
    .text-left {
      text-align: left;
      padding: 10px 14px;
      word-break: break-word;
    }
  }

  .section-approval {
    display: grid;
    grid-template-columns: 1fr auto;
    width: 100%;
    align-items: flex-start;

    .header-left-area {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
      padding-top: 20px;
    }

    .table-approval-field {
      width: auto;
      margin-left: 10px;
      tr:last-child {
        th,
        td {
          border-bottom: 1px solid #757575;
        }
      }
    }
  }
}

@media print {
  section {
    page-break-inside: avoid;
    page-break-after: auto;
  }
  .section-inspect {
    page-break-inside: auto !important;
  }
  tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }
  .page-break {
    page-break-inside: avoid;
    page-break-after: auto;
  }
}
</style>
